import type { ResearchConfigInput, ResearchLocation } from '@shared/config';
import { listCountries } from '@shared/normalize/location';
import { Plus, X } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Button } from '../../components/ui/button';
import { Alert } from '../../components/ui/feedback';
import { Field, Input, Select } from '../../components/ui/form';

/** Built once at module scope: the ISO country list is static. */
const countries = listCountries();

export interface StepSearchProps {
  config: ResearchConfigInput;
  onChange: (patch: Partial<ResearchConfigInput>) => void;
  errors: Record<string, string>;
}

/** Step 2. Keywords, locations and how many results to aim for per source. */
export function StepSearch({ config, onChange, errors }: StepSearchProps) {
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState<ResearchLocation>({ country: 'EG' });

  const keywords = config.search?.keywords ?? [];
  const locations = config.search?.locations ?? [];
  const search = config.search ?? {};

  const patchSearch = (patch: Partial<NonNullable<ResearchConfigInput['search']>>): void => {
    onChange({ search: { ...search, ...patch } });
  };

  const addKeyword = (): void => {
    const value = keyword.trim();
    if (!value || keywords.includes(value)) return;
    patchSearch({ keywords: [...keywords, value] });
    setKeyword('');
  };

  const addLocation = (): void => {
    const entry: ResearchLocation = {
      country: location.country,
      ...(location.city?.trim() ? { city: location.city.trim() } : {}),
      ...(location.area?.trim() ? { area: location.area.trim() } : {}),
    };
    const exists = locations.some(
      (item) => item.country === entry.country && item.city === entry.city && item.area === entry.area,
    );
    if (exists) return;
    patchSearch({ locations: [...locations, entry] });
    setLocation({ country: entry.country });
  };

  const onKeywordKey = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addKeyword();
    }
  };

  return (
    <div className="space-y-5">
      <Field label="Research name" error={errors['name']} required>
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            value={config.name ?? ''}
            invalid={invalid}
            aria-describedby={describedBy}
            onChange={(event) => onChange({ name: event.target.value })}
            placeholder="Restaurants and cafes in Alexandria"
          />
        )}
      </Field>

      <div>
        <Field label="Keywords / business types" hint="Press Enter to add. Each keyword is searched in every location." error={errors['keywords']}>
          {({ id, describedBy, invalid }) => (
            <span className="flex gap-2">
              <Input
                id={id}
                value={keyword}
                invalid={invalid}
                aria-describedby={describedBy}
                onChange={(event) => setKeyword(event.target.value)}
                onKeyDown={onKeywordKey}
                placeholder="restaurants"
              />
              <Button type="button" variant="outline" icon={<Plus className="h-3.5 w-3.5" />} onClick={addKeyword}>
                Add
              </Button>
            </span>
          )}
        </Field>
        {keywords.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {keywords.map((value) => (
              <li key={value}>
                <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[13px] text-foreground">
                  {value}
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    aria-label={`Remove keyword ${value}`}
                    onClick={() => patchSearch({ keywords: keywords.filter((item) => item !== value) })}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div>
        <p className="text-[13px] font-medium text-foreground">
          Locations<span className="ml-0.5 text-destructive">*</span>
        </p>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          Add a country, and optionally a city or district to search a narrower area.
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
          <label className="block">
            <span className="sr-only">Country</span>
            <Select value={location.country} onChange={(event) => setLocation((current) => ({ ...current, country: event.target.value }))}>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="sr-only">City</span>
            <Input
              value={location.city ?? ''}
              placeholder="City (optional)"
              onChange={(event) => setLocation((current) => ({ ...current, city: event.target.value }))}
            />
          </label>
          <label className="block">
            <span className="sr-only">District or area</span>
            <Input
              value={location.area ?? ''}
              placeholder="District (optional)"
              onChange={(event) => setLocation((current) => ({ ...current, area: event.target.value }))}
            />
          </label>
          <Button type="button" variant="outline" icon={<Plus className="h-3.5 w-3.5" />} onClick={addLocation}>
            Add
          </Button>
        </div>
        {errors['locations'] ? (
          <Alert className="mt-2" tone="error">
            {errors['locations']}
          </Alert>
        ) : null}
        {locations.length > 0 ? (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {locations.map((item, index) => {
              const label = [item.area, item.city, item.country].filter(Boolean).join(', ');
              return (
                <li key={`${label}-${String(index)}`}>
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[13px] text-foreground">
                    {label}
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove location ${label}`}
                      onClick={() => patchSearch({ locations: locations.filter((_, position) => position !== index) })}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Target results per source" hint="A ceiling, not a promise — sources often hold fewer matching listings.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              type="number"
              min={1}
              max={10000}
              className="tabular"
              aria-describedby={describedBy}
              value={search.targetPerSource ?? 250}
              onChange={(event) => patchSearch({ targetPerSource: Number(event.target.value) })}
            />
          )}
        </Field>
        <Field label="Result language">
          {({ id }) => (
            <Select
              id={id}
              value={search.language ?? 'auto'}
              onChange={(event) => patchSearch({ language: event.target.value as 'auto' | 'en' | 'ar' | 'fr' })}
            >
              <option value="auto">Automatic</option>
              <option value="en">English</option>
              <option value="ar">Arabic</option>
              <option value="fr">French</option>
            </Select>
          )}
        </Field>
      </div>
    </div>
  );
}
